<template>
  <div class="container role-cards-block">
    <div class="row role-cards-block__row">
      <div
        v-for="(card, index) in cards"
        :key="`role-card_${index}`"
        class="col-12 col-sm-6 role-cards-block__col">
        <div
          class="role-cards-block__card"
          :class="`role-cards-block__card_${card.theme || 'green'}`">
          <component
            v-if="card.title"
            :is="card.title_tag || 'h2'"
            class="role-cards-block__title">{{ card.title }}</component>

          <default-button
            v-if="card.button_text"
            class="role-cards-block__apply"
            @click.prevent="openApply(card)">
            {{ card.button_text }}
          </default-button>

          <p v-if="card.intro" class="role-cards-block__intro">{{ card.intro }}</p>

          <div v-if="card.role_label || card.salary_value" class="role-cards-block__salary">
            <div class="role-cards-block__salary-info">
              <div v-if="card.role_label" class="role-cards-block__salary-role">{{ card.role_label }}</div>
              <!-- Linia verticala din design e una singura, continua, de-a lungul
                   tuturor randurilor de dedesubt — de aceea bordura sta pe acest
                   invelis, nu pe fiecare bloc in parte. -->
              <div
                v-if="card.salary_value || card.salary_extra"
                class="role-cards-block__salary-lined">
                <div v-if="card.salary_value" class="role-cards-block__salary-value">{{ card.salary_value }}</div>
                <div v-if="card.salary_note" class="role-cards-block__salary-note">{{ card.salary_note }}</div>
                <div v-if="card.salary_extra" class="role-cards-block__salary-extra">{{ card.salary_extra }}</div>
              </div>
            </div>
            <lazy-image
              v-if="card.salary_img_src"
              class="role-cards-block__salary-image"
              cover_img
              :src="card.salary_img_src"
              :lazy_src="card.salary_lazy_img_src"
              alt="role"/>
          </div>

          <p v-if="card.tagline" class="role-cards-block__tagline">
            <target-icon class="role-cards-block__tagline-icon"/>
            <span>{{ card.tagline }}</span>
          </p>

          <html-block
            v-if="card.content"
            class="role-cards-block__content"
            :content="card.content"/>

          <div v-if="card.col_left || card.col_right" class="role-cards-block__columns">
            <html-block
              v-if="card.col_left"
              class="role-cards-block__column"
              :content="card.col_left"/>
            <html-block
              v-if="card.col_right"
              class="role-cards-block__column"
              :content="card.col_right"/>
          </div>

          <div
            v-if="card.video_img_src"
            class="role-cards-block__video"
            :class="{cursor_pointer: card.you_tube_src}"
            @click="openVideo(card)">
            <video-preview-with-play-btn
              :image_src="card.video_img_src"
              :image_lazy_src="card.video_lazy_img_src"
              :hide_button="!Boolean(card.you_tube_src)"/>
          </div>
        </div>
      </div>
    </div>
    <video-modal ref="video_modal"/>
    <acquisitions-apply-modal ref="apply_modal" :offices="offices"/>
  </div>
</template>

<script>
import HtmlBlock from "../small_parts/HtmlBlock";
import LazyImage from "../small_parts/LazyImage.vue";
import DefaultButton from "../slots/DefaultButton.vue";
import VideoPreviewWithPlayBtn from "../small_parts/VideoPreviewWithPlayBtn.vue";
import VideoModal from "../modals/VideoModal.vue";
import TargetIcon from "../icons/TargetIcon.vue";
import AcquisitionsApplyModal from "../modals/AcquisitionsApplyModal.vue";

// Admin-editable, free-text language labels per office — prone to typos since
// nothing is validated against a catalog, but kept deliberately simple per
// client's own call. A label like "Polish + English" is one selectable
// option in the form; it's split on "+" into discrete values before being
// sent to Zapier, so the payload still carries atomic language values.
const DEFAULT_OFFICES = [
  {id: 'iasi', title: 'Iași', languages: [{label: 'English'}, {label: 'Italian'}]},
  {id: 'constanta', title: 'Constanța', languages: [{label: 'English'}, {label: 'Spanish'}]},
  {id: 'brasov', title: 'Brașov', languages: [{label: 'English'}, {label: 'Hungarian'}, {label: 'German'}]},
  {
    id: 'katowice',
    title: 'Katowice',
    languages: [
      {label: 'Polish'},
      {label: 'Polish + English'},
      {label: 'German'},
      {label: 'German + English'},
      {label: 'Ukrainian'},
    ],
  },
  {id: 'sofia', title: 'Sofia', languages: [{label: 'English'}, {label: 'Greek'}]},
  {id: 'chisinau', title: 'Chișinău', languages: [{label: 'English'}]},
  {id: 'ploiesti', title: 'Ploiești', languages: [{label: 'English'}]},
]

export default {
  name: "RoleCardsBlock",
  components: {
    AcquisitionsApplyModal,
    TargetIcon,
    VideoModal,
    VideoPreviewWithPlayBtn,
    DefaultButton,
    LazyImage,
    HtmlBlock
  },
  props: {
    /**props**/
    cards: {
      type: Array,
      default: () => [],
      admin_type: 'array',
      array_values: {
        theme: {
          type: String,
          default: 'green',
          admin_type: 'select',
          admin_data_values: [
            'green',
            'light'
          ],
        },
        title: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        title_tag: {
          type: String,
          default: 'h2',
          admin_type: 'select',
          admin_data_values: [
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
          ],
        },
        button_text: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        button_link: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        intro: {
          type: String,
          default: '',
          admin_type: 'textarea',
        },
        role_label: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        salary_value: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        salary_note: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        salary_extra: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        salary_img_src: {
          type: String,
          default: '',
          admin_type: 'image',
        },
        salary_lazy_img_src: {
          type: String,
          default: '',
          admin_type: 'image',
        },
        tagline: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        content: {
          type: String,
          default: '',
          admin_type: 'editor',
        },
        col_left: {
          type: String,
          default: '',
          admin_type: 'editor',
        },
        col_right: {
          type: String,
          default: '',
          admin_type: 'editor',
        },
        video_img_src: {
          type: String,
          default: '',
          admin_type: 'image',
        },
        video_lazy_img_src: {
          type: String,
          default: '',
          admin_type: 'image',
        },
        you_tube_src: {
          type: String,
          default: '',
          admin_type: 'input',
        },
      }
    },
    offices: {
      type: Array,
      default: () => DEFAULT_OFFICES,
      admin_type: 'array',
      array_values: {
        id: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        title: {
          type: String,
          default: '',
          admin_type: 'input',
        },
        languages: {
          type: Array,
          default: () => [],
          admin_type: 'array',
          array_values: {
            label: {
              type: String,
              default: '',
              admin_type: 'input',
            },
          }
        },
      }
    },
    /**props**/
  },
  methods: {
    openVideo({you_tube_src}) {
      if (!you_tube_src) return
      this.$refs.video_modal.openModal(you_tube_src)
    },
    openApply({role_label}) {
      this.$refs.apply_modal.openModal((role_label || '').toLowerCase())
    }
  }
}
</script>
